import { CalendarIcon } from 'lucide-react'
import { useState } from 'react'
import * as m from '~/paraglide/messages'
import { formatLongDate } from '~/shared/lib/date-locale'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Calendar } from '~/shared/ui/calendar'
import { Input } from '~/shared/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '~/shared/ui/popover'

interface DatePickerProps {
  value?: string // YYYY-MM-DD
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  id?: string
  name?: string
}

function formatDate(date: Date | undefined) {
  if (!date || Number.isNaN(date.getTime())) return ''
  return formatLongDate(date)
}

function dateToYMD(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function DatePicker({ value, onChange, placeholder, className, id, name }: DatePickerProps) {
  const [open, setOpen] = useState(false)

  const dateValue = value ? new Date(`${value}T00:00:00`) : undefined
  const [month, setMonth] = useState<Date | undefined>(dateValue)

  // The year dropdown otherwise defaults to ending at the current year. Allow up
  // to five years ahead (events can be scheduled well in advance), and always
  // include the selected year so editing an older/further date never clamps it.
  const currentYear = new Date().getFullYear()
  const selectedYear =
    dateValue && !Number.isNaN(dateValue.getTime()) ? dateValue.getFullYear() : currentYear
  const startMonth = new Date(Math.min(currentYear - 5, selectedYear), 0)
  const endMonth = new Date(Math.max(currentYear + 5, selectedYear), 11)

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return
    onChange(dateToYMD(date))
    setOpen(false)
  }

  return (
    <div className={cn('relative', className)}>
      <Input
        id={id}
        name={name}
        value={formatDate(dateValue)}
        placeholder={placeholder || m.selectDate()}
        className="bg-background pr-10 cursor-pointer"
        readOnly
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="absolute top-1/2 right-2 size-6 -translate-y-1/2">
            <CalendarIcon className="size-3.5" />
            <span className="sr-only">{m.selectDate()}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="end">
          <Calendar
            mode="single"
            selected={dateValue}
            captionLayout="dropdown"
            month={month}
            onMonthChange={setMonth}
            onSelect={handleDateSelect}
            startMonth={startMonth}
            endMonth={endMonth}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
